import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { dataFetch } from '../../api';
import { useAuth } from '../../context/AuthContext';

const { FiX, FiFileText, FiLoader, FiCheck } = FiIcons;

const CreatePolicyModal = ({ onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(true);

  const [formData, setFormData] = useState({
    policy_name: '',
    version: '1.0',
    details: '',
    module_ids: []
  });

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await dataFetch('read/modules?limit=100');
        if (res?.status === 'success') {
          setModules(res.data || []);
        }
      } catch (error) {
        console.error("Failed to load modules", error);
      } finally {
        setLoadingModules(false);
      }
    };
    fetchModules();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleModuleToggle = (moduleId) => {
    setFormData(prev => {
      const currentIds = prev.module_ids;
      if (currentIds.includes(moduleId)) {
        return { ...prev, module_ids: currentIds.filter(id => id !== moduleId) };
      } else {
        return { ...prev, module_ids: [...currentIds, moduleId] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.policy_name.trim()) return;

    setLoading(true);
    try {
      const payload = {
        policy_name: formData.policy_name,
        version: formData.version,
        details: formData.details,
        // Store as comma-separated string for NoCodeBackend simplicity
        module_ids: formData.module_ids.join(','),
        user_id: currentUser?.id || 'system'
      };

      const res = await dataFetch('create/policies', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (res?.status === 'success') {
        onSuccess(res.data);
      } else {
        alert(res?.error || 'Failed to create policy');
      }
    } catch (error) {
      console.error("Error creating policy:", error);
      alert('An error occurred while creating the policy.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col"
      >
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 shrink-0">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <SafeIcon icon={FiFileText} className="text-blue-600" />
            Create New Policy
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <form id="create-policy-form" onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Policy Name *</label>
                <input
                  type="text"
                  name="policy_name"
                  value={formData.policy_name}
                  onChange={handleChange}
                  placeholder="e.g. Data Protection Policy"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Version</label>
                <input
                  type="text"
                  name="version"
                  value={formData.version}
                  onChange={handleChange}
                  placeholder="1.0"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description / Details</label>
              <textarea
                name="details"
                value={formData.details}
                onChange={handleChange}
                placeholder="Brief description of what this policy covers..."
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm min-h-[80px]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Linked Training Modules</label>
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                {loadingModules ? (
                  <div className="p-4 text-center text-slate-500 flex items-center justify-center gap-2 text-sm">
                    <SafeIcon icon={FiLoader} className="animate-spin" /> Loading modules...
                  </div>
                ) : modules.length === 0 ? (
                  <div className="p-4 text-center text-slate-500 text-sm">No modules available.</div>
                ) : (
                  <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                    {modules.map(module => {
                      const isChecked = formData.module_ids.includes(String(module.id));
                      return (
                        <label key={module.id} className="group flex items-center gap-3 p-3 hover:bg-blue-50 cursor-pointer transition-colors">
                          <div className="relative flex items-center justify-center">
                            <input
                              type="checkbox"
                              className={`w-4 h-4 rounded border border-slate-300 focus:ring-2 focus:ring-blue-500 cursor-pointer peer appearance-none checked:bg-blue-600 checked:border-blue-600 transition-all ${isChecked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                              checked={isChecked}
                              onChange={() => handleModuleToggle(String(module.id))}
                            />
                            <SafeIcon icon={FiCheck} className="absolute text-white text-[10px] pointer-events-none opacity-0 peer-checked:opacity-100" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{module.title}</p>
                            <p className="text-xs text-slate-500 truncate">{module.category || 'Uncategorized'}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">Select the training modules that cover this policy.</p>
            </div>
          </form>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="create-policy-form"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-sm"
          >
            {loading ? <><SafeIcon icon={FiLoader} className="animate-spin" /> Saving...</> : 'Create Policy'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default CreatePolicyModal;